import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getSystemConfig, updateMFAPolicy } from '../../services/systemConfigService';
import { SystemConfig } from '../../types/systemConfig';
import toast from 'react-hot-toast';
import { ShieldCheckIcon, ClockIcon, UserGroupIcon } from '@heroicons/react/24/outline';

const MFAPolicySettings: React.FC = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [config, setConfig] = useState<SystemConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form state
  const [mfaRequired, setMfaRequired] = useState(false);
  const [gracePeriodDays, setGracePeriodDays] = useState(7);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const systemConfig = await getSystemConfig();
      if (systemConfig) {
        setConfig(systemConfig);
        setMfaRequired(systemConfig.mfaPolicy.required);
        setGracePeriodDays(systemConfig.mfaPolicy.gracePeriodDays);
      }
    } catch (error) {
      console.error('Error loading system config:', error);
      toast.error('Hiba a konfiguráció betöltése során');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!user?.id) return;

    try {
      setSaving(true);

      await updateMFAPolicy(
        {
          required: mfaRequired,
          gracePeriodDays: gracePeriodDays
        },
        user.id
      );

      toast.success('MFA szabályzat sikeresen mentve!', {
        icon: '✅',
      });

      // Reload config
      await loadConfig();
    } catch (error) {
      console.error('Error saving MFA policy:', error);
      toast.error('Hiba a mentés során');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-500 to-pink-600 rounded-2xl p-6 text-white">
        <div className="flex items-center space-x-3">
          <div className="p-3 bg-white/20 rounded-xl">
            <ShieldCheckIcon className="h-8 w-8" />
          </div>
          <div>
            <h2 className="text-2xl font-bold">MFA Szabályzat</h2>
            <p className="text-red-100 mt-1">
              Rendszer szintű kétfaktoros hitelesítés beállítások
            </p>
          </div>
        </div>
      </div>

      {/* Warning */}
      {mfaRequired && (
        <div className="bg-orange-50 dark:bg-orange-900/20 border-2 border-orange-200 dark:border-orange-800 rounded-xl p-4">
          <div className="flex items-start space-x-3">
            <ShieldCheckIcon className="h-6 w-6 text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-orange-900 dark:text-orange-300">
                ⚠️ MFA kötelező az összes felhasználónak
              </h3>
              <p className="text-sm text-orange-800 dark:text-orange-400 mt-1">
                Az új felhasználóknak {gracePeriodDays} napjuk van az MFA bekapcsolására.
                Az ügyfelek (customer role) mentesek a kötelező MFA alól.
              </p>
              {config?.mfaPolicy.enforcedAt && (
                <p className="text-xs text-orange-700 dark:text-orange-500 mt-2">
                  Kötelezővé téve: {new Date(config.mfaPolicy.enforcedAt).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Settings Card */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="p-6 space-y-6">
          {/* MFA Required Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
            <div className="flex items-start space-x-3">
              <ShieldCheckIcon className="h-6 w-6 text-primary-600 dark:text-primary-400 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  MFA kötelező minden felhasználónak
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Ha bekapcsolod, minden felhasználónak (admin, user) be kell kapcsolnia a kétfaktoros hitelesítést.
                  Az ügyfelek (customer) automatikusan mentesek.
                </p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={mfaRequired}
                onChange={(e) => setMfaRequired(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></div>
            </label>
          </div>

          {/* Grace Period */}
          <div className="flex items-start space-x-3 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
            <ClockIcon className="h-6 w-6 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1" />
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                Türelmi időszak (napokban)
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                Az új felhasználóknak ennyi napjuk van az MFA bekapcsolására, mielőtt kizárásra kerülnének.
              </p>
              <div className="flex items-center space-x-3">
                <input
                  type="number"
                  min="1"
                  max="30"
                  value={gracePeriodDays}
                  onChange={(e) => setGracePeriodDays(parseInt(e.target.value) || 7)}
                  className="w-24 px-4 py-2 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                />
                <span className="text-sm text-gray-600 dark:text-gray-400">nap</span>
              </div>
            </div>
          </div>

          {/* Exempt Roles Info */}
          <div className="flex items-start space-x-3 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
            <UserGroupIcon className="h-6 w-6 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-semibold text-blue-900 dark:text-blue-300 mb-2">
                Kivételek
              </h3>
              <p className="text-sm text-blue-800 dark:text-blue-400">
                Az alábbi szerepkörök <strong>mentesek</strong> a kötelező MFA alól:
              </p>
              <ul className="mt-2 space-y-1">
                <li className="text-sm text-blue-700 dark:text-blue-300">
                  • <strong>Customer</strong> - Ügyfelek (ügyfélportál felhasználók)
                </li>
              </ul>
              <p className="text-xs text-blue-600 dark:text-blue-500 mt-3">
                A super_admin, admin és user szerepköröknek kötelező az MFA, ha be van kapcsolva.
              </p>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-3 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white rounded-xl font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-primary-500/30"
            >
              {saving ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Mentés...</span>
                </div>
              ) : (
                'Szabályzat mentése'
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Statistics */}
      {config && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4">
            Információk
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
              <p className="text-sm text-gray-600 dark:text-gray-400">Utoljára frissítve</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
                {config.updatedAt.toLocaleDateString()}
              </p>
            </div>
            <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
              <p className="text-sm text-gray-600 dark:text-gray-400">Státusz</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
                {mfaRequired ? (
                  <span className="text-green-600 dark:text-green-400">✅ Kötelező</span>
                ) : (
                  <span className="text-gray-500 dark:text-gray-400">⚪ Opcionális</span>
                )}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MFAPolicySettings;
